import { ref, computed, watch } from 'vue';
import { useDeliveryFee } from './useDeliveryFee';
import { usePromotions } from './usePromotions';
import { calculateIvaBreakdown } from '@/utils/ivaHelper';

export function useOrderCalculations(
  persons: any,
  form: any,
  ivaRate: any,
  selectedCustomer: any,
  selectedAddress: any
) {
  // IVA state
  const ivaEnabled = ref(false);
  const ivaAmount = ref(0);

  // Delivery fee calculation composable
  const { calculateDeliveryFeeForOrder, loading: deliveryFeeLoading } = useDeliveryFee();
  const deliveryFeeValue = ref(0);

  // Promotions composable
  const { calculatePromotionsForOrder, getTotalDiscount, getAppliedPromotions, getPromotionMessages } = usePromotions();
  const appliedPromotions = ref<any[]>([]);
  const promotionMessages = ref<string[]>([]);
  const isCalculatingPromotions = ref(false);
  const currentDiscount = ref(0);

  // Calculate IVA amount using centralized helper
  const calculateIvaAmount = (subtotalAmount: number, discountAmount: number, deliveryFeeAmount: number, rate: number, enabled: boolean) => {
    return calculateIvaBreakdown({
      subtotal: subtotalAmount,
      discount: discountAmount,
      deliveryFee: deliveryFeeAmount,
      ivaRate: rate,
      enabled
    }).ivaAmount;
  };

  // Calculate order total using correct IVA formula
  const orderTotal = computed(() => {
    const baseTotal = persons.value.reduce((sum: number, person: any) => {
      return sum + person.items.reduce((itemSum: number, item: any) => {
        return itemSum + ((item.unit_price || 0) * item.quantity);
      }, 0);
    }, 0);

    const deliveryFee = form.value.type === 'Delivery' ? deliveryFeeValue.value : 0;
    const ivaResult = calculateIvaBreakdown({
      subtotal: baseTotal,
      discount: currentDiscount.value,
      deliveryFee: deliveryFee,
      ivaRate: ivaRate.value,
      enabled: ivaEnabled.value
    });

    return ivaResult.totalWithIva;
  });

  // Calculate final total using centralized helper
  const finalTotal = computed(() => {
    const subtotal = persons.value.reduce((sum: number, person: any) => {
      return sum + person.items.reduce((itemSum: number, item: any) => {
        return itemSum + ((item.unit_price || 0) * item.quantity);
      }, 0);
    }, 0);
    
    const deliveryFee = form.value.type === 'Delivery' ? deliveryFeeValue.value : 0;
    const ivaResult = calculateIvaBreakdown({
      subtotal: subtotal,
      discount: currentDiscount.value,
      deliveryFee: deliveryFee,
      ivaRate: ivaRate.value,
      enabled: ivaEnabled.value
    });
    
    return Math.max(0, ivaResult.totalWithIva);
  });

  // Calculate base total before promotions (for display)
  const baseTotalForDisplay = computed(() => {
    return orderTotal.value;
  });

  // Calculate subtotal (order total without IVA)
  const subtotal = ref(0);

  // Update subtotal when persons change
  watch(() => persons.value, () => {
    const calculatedSubtotal = persons.value.reduce((sum: number, person: any) => {
      return sum + person.items.reduce((itemSum: number, item: any) => {
        return itemSum + ((item.unit_price || 0) * item.quantity);
      }, 0);
    }, 0);
    
    subtotal.value = calculatedSubtotal;
  }, { immediate: true, deep: true });

  // Update IVA amount when IVA settings, subtotal, discount or delivery fee changes
  watch([ivaEnabled, ivaRate, subtotal, currentDiscount, deliveryFeeValue], ([enabled, rate, subtotalAmount, discountAmount, deliveryFeeAmount]) => {
    ivaAmount.value = calculateIvaAmount(subtotalAmount, discountAmount, deliveryFeeAmount, rate, enabled);
  }, { immediate: true });

  // IVA handler functions
  const handleToggleIva = (enabled: boolean) => {
    ivaEnabled.value = enabled;
  };

  // Delivery fee calculation with debouncing
  let deliveryFeeTimeout: NodeJS.Timeout | null = null;
  const DELIVERY_FEE_DEBOUNCE_MS = 300;

  const calculateCurrentDeliveryFee = async (immediate = false) => {
    if (form.value.type !== 'Delivery') {
      deliveryFeeValue.value = 0;
      return;
    }

    // Don't calculate delivery fee if subtotal is 0 (no products)
    if (subtotal.value <= 0) {
      deliveryFeeValue.value = 0;
      return;
    }

    // Clear existing timeout
    if (deliveryFeeTimeout) {
      clearTimeout(deliveryFeeTimeout);
      deliveryFeeTimeout = null;
    }

    const executeCalculation = async () => {
      try {
        const addressData = selectedAddress.value ? {
          address: selectedAddress.value.address,
          neighborhood: selectedAddress.value.neighborhood,
          city: selectedAddress.value.city
        } : undefined;

        const hasPromotion = appliedPromotions.value && appliedPromotions.value.length > 0;
        const fee = await calculateDeliveryFeeForOrder(
          form.value.type,
          subtotal.value,
          addressData,
          hasPromotion
        );
        
        deliveryFeeValue.value = fee;
      } catch (error) {
        console.error('Error calculating delivery fee:', error);
        deliveryFeeValue.value = 0;
      }
    };

    if (immediate) {
      // Execute immediately for critical cases (like edit mode loading)
      executeCalculation();
    } else {
      // Debounce for rapid changes (like user typing)
      deliveryFeeTimeout = setTimeout(executeCalculation, DELIVERY_FEE_DEBOUNCE_MS);
    }
  };

  // Watch for changes that affect delivery fee calculation
  watch([
    () => form.value.type,
    () => subtotal.value,
    () => ivaEnabled.value,
    () => ivaRate.value,
    () => selectedCustomer.value?.id, // Only monitor customer ID, not entire object
    () => selectedAddress.value?.id  // Only monitor address ID, not entire object
  ], ([
    newOrderType,
    newSubtotal,
    newIvaEnabled,
    newIvaRate,
    newCustomerId,
    newAddressId
  ], [
    oldOrderType,
    oldSubtotal,
    oldIvaEnabled,
    oldIvaRate,
    oldCustomerId,
    oldAddressId
  ]) => {
    // Only log and calculate if something actually changed that affects delivery fee
    const hasRelevantChange = (
      newOrderType !== oldOrderType ||
      newSubtotal !== oldSubtotal ||
      newCustomerId !== oldCustomerId ||
      newAddressId !== oldAddressId
    );
    
    if (hasRelevantChange) {
      calculateCurrentDeliveryFee();
    }
  }, { immediate: true });

  // Calculate promotions in real-time
  watch(
    [() => persons.value, () => subtotal.value],
    async () => {
      if (!persons.value || persons.value.length === 0) {
        currentDiscount.value = 0;
        appliedPromotions.value = [];
        promotionMessages.value = [];
        return;
      }

      // Collect all items from all persons
      const allItems: any[] = [];
      persons.value.forEach((person: any) => {
        person.items.forEach((item: any) => {
          if (item.quantity > 0) {
            allItems.push({
              menu_item_id: item.menu_item_id,
              variant_id: item.variant_id || undefined,
              quantity: item.quantity,
              unit_price: item.unit_price || 0
            });
          }
        });
      });

      if (allItems.length === 0) {
        currentDiscount.value = 0;
        appliedPromotions.value = [];
        promotionMessages.value = [];
        return;
      }

      try {
        isCalculatingPromotions.value = true;
        
        const promotionResult = await calculatePromotionsForOrder(
          allItems,
          new Date().toISOString(),
          subtotal.value + deliveryFeeValue.value
        );
        
        currentDiscount.value = promotionResult.total_discount;
        appliedPromotions.value = promotionResult.applied_promotions;
        promotionMessages.value = promotionResult.applied_promotions.map((p: any) => p.message);
      } catch (error) {
        console.error('❌ [PROMOTIONS] Real-time calculation error:', error);
        currentDiscount.value = 0;
        appliedPromotions.value = [];
        promotionMessages.value = [];
      } finally {
        isCalculatingPromotions.value = false;
      }
    },
    { immediate: true, deep: true }
  );

  // Reactive delivery fee value
  const deliveryFee = computed(() => {
    let fee = deliveryFeeValue.value;
    return fee;
  });

  return {
    // IVA
    ivaEnabled,
    ivaAmount,
    handleToggleIva,
    
    // Totals
    orderTotal,
    finalTotal,
    baseTotalForDisplay,
    subtotal,
    
    // Delivery fee
    deliveryFee,
    deliveryFeeLoading,
    calculateCurrentDeliveryFee,
    
    // Promotions
    currentDiscount,
    appliedPromotions,
    promotionMessages,
    isCalculatingPromotions
  };
}
